import Button from '@/components/Button';
import Breadcrumbs from '@/componentsDS/Breadcrumbs/index.jsx';
import DataTable from '@/componentsDS/DataTable/index';
import InputDropdownDS from '@/componentsDS/InputDropdown/index';
import CardConfigure from '@/componentsDS/Modal/CardConfigure';
import Modal from '@/componentsDS/Modal/Modal';
import Search from '@/componentsDS/Search/index';
import Tags from '@/componentsDS/Tags/Tags';
import { ToastContext } from '@/context/toast/index.js';
import useModal from '@/hooks/use-modal/index';
import usePage from '@/hooks/use-page';
import { useNavigate } from '@/lib/router-dom';
import useConfig from '@/hooks/use-config/index';
import useSubmittalsDataElements from '@/hooks/use-submittals/useSubmittalsDataElements';
import useSubmittalsVisibility from '@/hooks/use-submittals/useSubmittalsVisibility';
import useSubmittals from '@/hooks/use-submittals/useSubmittals';
import useAllowedReceiverCheck from '@/hooks/use-submittals/useAllowedReceiverCheck';
import submittalsService from '@/services/api/submittalsService.js';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReactComponent as CheckDouble } from '@/assets/svgs/DesignSystem/check-double.svg';
import { ReactComponent as CheckHistory } from '@/assets/svgs/DesignSystem/check-history.svg';
import { ReactComponent as IconInfo } from '@/assets/svgs/DesignSystem/DS_Icon_info.svg';
import { ReactComponent as XCircle } from '@/assets/svgs/DesignSystem/x-circle.svg';
import { ReactComponent as Clock } from '@/assets/svgs/DesignSystem/clock.svg';
import { ReactComponent as IconInfoEmpty } from '@/assets/svgs/icon-info.svg';
import moment from 'moment';
import { NON_EDITABLE_STATUSES, SUBMITTAL_STATUS } from './constants';

const STATUS_TAG_PROPS = {
	DRAFT:          { label: 'Draft',          prefixIcon: <CheckHistory />,      tagClassName: 'DS__tag--neutral'  },
	PENDING_REVIEW: { label: 'Pending Review', prefixIcon: <CheckHistory />,      tagClassName: 'DS__tag--warning'  },
	APPROVED:       { label: 'Approved',       prefixIcon: <CheckDouble />,       tagClassName: 'DS__tag--success'  },
	REJECTED:        { label: 'Rejected',         prefixIcon: <XCircle />, tagClassName: 'DS__tag--critical' },
	REPLACED:        { label: 'Replaced',         prefixIcon: <XCircle />, tagClassName: 'DS__tag--neutral'  },
	DELETED:         { label: 'Deleted',          prefixIcon: <Clock />,   tagClassName: 'DS__tag--neutral'  },
	PASSED_DEADLINE: { label: 'Passed Deadline',  prefixIcon: <Clock />,   tagClassName: 'DS__tag--critical' },
};

// Maps UI column key → backend sort column name (only sortable columns)
const SORT_COLUMN_MAP = {
	dataElement:     'data_element_id',
	submissionMonth: 'submission_month',
	modified:        'modified',
	projectContract: 'project_contract_name',
	status:          'status',
};

const SubmittalsPage = () => {
	const { dataElements, isSubmittalsEnabled } = useSubmittalsDataElements();
	const { showSubmittals } = useSubmittalsVisibility();
	const { provideItems } = useConfig();
	const { checkAllowedReceiver, configProducesReady } = useAllowedReceiverCheck();
	const { addToastMessage } = useContext(ToastContext);
	const currentUserId = useSelector(state => state.auth.user?.id);
	const { visible: isDeleteModalVisible, show: showDeleteModal, hide: hideDeleteModal } = useModal();
	const [submittalToDelete, setSubmittalToDelete] = useState(null);
	const [isDeleting, setIsDeleting] = useState(false);
	const [canCreateSubmission, setCanCreateSubmission] = useState(false);

	// Filter options (from BE /filters). Data element options are derived from
	// raw IDs + dataElements/provideItems so the labels resolve correctly
	// regardless of which load arrives first.
	const [dataElementIds, setDataElementIds] = useState([]);
	const [selectedDataElementIds, setSelectedDataElementIds] = useState(() => new Set());
	const [projectContractOptions, setProjectContractOptions] = useState([]);
	const [submissionMonthOptions, setSubmissionMonthOptions] = useState([]);
	const [statusOptions, setStatusOptions] = useState([]);

	// Per-filter dropdown search text (controlled by InputDropdownDS)
	const [filterSearch, setFilterSearch] = useState({
		dataElement: '',
		projectContractName: '',
		submissionMonth: '',
		status: '',
	});

	// Search / sort / page state → sent to BE
	const [search, setSearch] = useState('');
	const [searchInput, setSearchInput] = useState(''); // debounced via timeout
	const [sortConfig, setSortConfig] = useState({ key: 'modified', direction: false });
	const [currentPage, setCurrentPage] = useState(1);

	const { goTo } = useNavigate();
	const { breadcrumbsItems } = usePage({
		title: 'Submittals',
		breadcrumbs: [
			{ label: '', link: '/', isHomePage: true },
			{ label: 'Submittals', link: '/submittals' },
		],
	});

	const getDataElementName = useCallback((dataElementId) => {
		const provideItem = provideItems.find(item => item.id === dataElementId);
		if (provideItem?.name) return provideItem.name;
		const de = dataElements.find(d => d.dataElementId === dataElementId);
		return de?.elementName || dataElementId || '-';
	}, [dataElements, provideItems]);

	const dataElementsOptions = useMemo(() => (
		dataElementIds.map(id => ({
			id,
			label: getDataElementName(id),
			$selected: selectedDataElementIds.has(id),
		}))
	), [dataElementIds, selectedDataElementIds, getDataElementName]);

	// Derive active filter values to pass to BE
	const activeFilters = useMemo(() => {
		const selected = (opts) => opts.filter(o => o.$selected).map(o => o.id);
		const de = Array.from(selectedDataElementIds);
		const months = selected(submissionMonthOptions);
		const statuses = selected(statusOptions);
		const orgs = selected(projectContractOptions);
		return {
			data_element_id:      de.length > 0 ? de : undefined,
			submission_month:     months.length > 0 ? months : undefined,
			status:               statuses.length > 0 ? statuses : undefined,
			project_contract_number: orgs.length > 0 ? orgs : undefined,
		};
	}, [selectedDataElementIds, submissionMonthOptions, statusOptions, projectContractOptions]);

	const sortParams = useMemo(() => ({
		sort:  sortConfig.key ? SORT_COLUMN_MAP[sortConfig.key] : undefined,
		order: sortConfig.direction === true ? 'asc' : sortConfig.direction === false && sortConfig.key ? 'desc' : undefined,
	}), [sortConfig]);

	const { submittals, meta, loading, error, refetch } = useSubmittals(isSubmittalsEnabled, {
		page: currentPage,
		search,
		sort: sortParams.sort,
		order: sortParams.order,
		filters: activeFilters,
	});

	// Fetch allowed receivers and validate against config.produces on mount
	useEffect(() => {
		if (!isSubmittalsEnabled || !configProducesReady) return;
		checkAllowedReceiver('').then(hasMatch => setCanCreateSubmission(hasMatch));
	}, [isSubmittalsEnabled, configProducesReady]);

	// Load filter options from BE on mount
	useEffect(() => {
		if (!isSubmittalsEnabled) return;
		submittalsService.getFilters().then(([err, data]) => {
			if (err || !data) return;

			setDataElementIds(data.data_element_ids || []);
			setSubmissionMonthOptions(
				(data.submission_months || []).map(m => ({ id: m, label: m, $selected: false }))
			);
			setStatusOptions(
				(data.statuses || []).map(s => ({
					id: s,
					label: STATUS_TAG_PROPS[s]?.label || s,
					$selected: false,
				}))
			);
			setProjectContractOptions(
				(data.project_contracts || []).map(c => ({
					id: c.project_contract_number,
					label: c.project_contract_name,
					$selected: false,
				}))
			);
		});
	}, [isSubmittalsEnabled]);

	// Reset to page 1 when filters/search/sort change
	useEffect(() => {
		setCurrentPage(1);
	}, [selectedDataElementIds, projectContractOptions, submissionMonthOptions, statusOptions, search, sortConfig]);

	// Debounce search input
	useEffect(() => {
		const t = setTimeout(() => setSearch(searchInput), 400);
		return () => clearTimeout(t);
	}, [searchInput]);

	const totalPages = meta.totalPages || 1;
	const totalEntries = meta.total || 0;

	const handleSort = (key) => {
		if (!SORT_COLUMN_MAP[key]) return; // not sortable
		setSortConfig(prev => ({
			key,
			direction: prev.key === key ? !prev.direction : true,
		}));
	};

	const handleEdit = (submittal) => {
		goTo(`/submittals/${submittal.submittal_id}/edit`);
	};

	const handleDownloadCsv = async (submittal) => {
		const [err] = await submittalsService.downloadSubmittal(submittal.submittal_id, 'csv');
		if (err) addToastMessage(err, 'error');
	};

	const handleRemoveClick = (submittal) => {
		setSubmittalToDelete(submittal);
		showDeleteModal();
	};

	const handleDeleteConfirm = async () => {
		if (!submittalToDelete) return;
		setIsDeleting(true);
		const [err] = await submittalsService.deleteSubmittal(submittalToDelete.submittal_id);
		setIsDeleting(false);
		hideDeleteModal();
		setSubmittalToDelete(null);
		if (err) {
			addToastMessage(err, 'error');
		} else {
			addToastMessage('Draft removed successfully.', 'success');
			refetch();
		}
	};

	const getMenuItems = useCallback((submittal) => {
		const items = [];
		const isOwner = submittal.created_by && submittal.created_by === currentUserId;
		if (isOwner && !NON_EDITABLE_STATUSES.has(submittal.status)) {
			items.push({ label: submittal.status === SUBMITTAL_STATUS.REJECTED ? 'Update Submission' : 'Edit', onClick: () => handleEdit(submittal), id: 'submittals-edit-button-id' });
		}
		if (submittal.status !== SUBMITTAL_STATUS.DRAFT) {
			items.push({ label: 'Download as CSV', onClick: () => handleDownloadCsv(submittal), id: 'submittals-download-csv-button-id' });
		}
		if (submittal.status === SUBMITTAL_STATUS.DRAFT) {
			items.push({ label: 'Remove', onClick: () => handleRemoveClick(submittal) });
		}
		return items;
	}, [currentUserId]);

	const renderStatusCell = useCallback((status) => {
		const tagProps = STATUS_TAG_PROPS[status];
		if (tagProps) return <Tags {...tagProps} />;
		return status || '-';
	}, []);

	const formatModifiedDate = useCallback((modified) => {
		if (!modified) return '-';
		return moment(modified).format('DD/MM/YYYY h:mma');
	}, []);

	const tableColumns = useMemo(() => [
		{ key: 'dataElement',     label: 'Data Element',          sortable: true, render: (row) => getDataElementName(row.data_element_id) },
		{ key: 'submissionMonth', label: 'Submission Month',      sortable: true, render: (row) => row.submission_month || '-' },
		{ key: 'modified',        label: 'Last Modified',         sortable: true, render: (row) => formatModifiedDate(row.modified) },
		{ key: 'projectContract', label: 'Project Contract Name', sortable: true, render: (row) => row.payload?.project_details?.project_contract_name || '-' },
		{ key: 'status',          label: 'Status',                sortable: true, render: (row) => renderStatusCell(row.status) },
	], [getDataElementName, formatModifiedDate, renderStatusCell]);

	const deleteModalText = isDeleting ? "Removing..." : "Confirm";

	const showPagination = !loading && totalEntries > 0;

	const handleFlagFilterChange = (setter) => (selectedItems) => {
		const ids = new Set(selectedItems.map(s => s.id));
		setter(prev => prev.map(o => ({ ...o, $selected: ids.has(o.id) })));
	};

	const handleFilterSearchChange = (key) => (e) => {
		const next = typeof e === 'string' ? e : e?.target?.value ?? '';
		setFilterSearch(prev => ({ ...prev, [key]: next }));
	};

	const filterConfig = useMemo(() => [
		{
			id: 'dataElement',
			gaId: 'submittals-filter-data-element-id',
			label: 'Data Element',
			placeholder: 'Select Data Element',
			options: dataElementsOptions,
			selected: dataElementsOptions.filter(o => o.$selected),
			onSelect: (items) => setSelectedDataElementIds(new Set(items.map(s => s.id))),
		},
		{
			id: 'projectContractName',
			gaId: 'submittals-filter-project-contract-name-id',
			label: 'Project Contract Name',
			placeholder: 'Select Project Contract Name',
			options: projectContractOptions,
			selected: projectContractOptions.filter(o => o.$selected),
			onSelect: handleFlagFilterChange(setProjectContractOptions),
		},
		{
			id: 'submissionMonth',
			gaId: 'submittals-filter-submission-month-id',
			label: 'Submission Month',
			placeholder: 'Select Submission Month',
			options: submissionMonthOptions,
			selected: submissionMonthOptions.filter(o => o.$selected),
			onSelect: handleFlagFilterChange(setSubmissionMonthOptions),
		},
		{
			id: 'status',
			gaId: 'submittals-filter-status-id',
			label: 'Status',
			placeholder: 'Select Status',
			options: statusOptions,
			selected: statusOptions.filter(o => o.$selected),
			onSelect: handleFlagFilterChange(setStatusOptions),
		},
	], [dataElementsOptions, projectContractOptions, submissionMonthOptions, statusOptions]);

	if (!showSubmittals) return null;

	return (
		<div className="submittals-page">
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />

			<div className="title-header">
				<div className="title-header__left">
					<div>
						<div className="title-header__title">Submittals</div>
						<div className="title-header__subtitle">Submit E-Forms to be reviewed by external reviewers.</div>
					</div>
				</div>
				<div className="title-header__right">
					{canCreateSubmission && <div className="overview__filter overview__button_wrap">
						<Button
							round
							full
							text="Create New Submission"
							id="create-submission-button-id"
							submit={() => goTo('/submittals/create')}
						/>
					</div>}
				</div>
			</div>

			<div className="overview">
				<div className="overview_filter overview-filter__full-width">
					<div className="overview_filter__cols overview-filter__flex">
						{filterConfig.map(option => (
							<div key={option.id} className="overview_filter__col">
								<InputDropdownDS
									id={option.gaId}
									name={option.id}
									placeholder={option.selected.length > 0 ? ' ' : option.placeholder}
									dropdownOption={option.options}
									dropdownSelectedOption={option.selected}
									onSelectDropdownItem={option.onSelect}
									value={filterSearch[option.id]}
									onChange={handleFilterSearchChange(option.id)}
									multipleSelection
								/>
							</div>
						))}
					</div>
				</div>

				<div className="submittals-search" id="submittals-search-id">
					<Search
						value={searchInput}
						placeholder="Search"
						onChange={e => setSearchInput(e?.target?.value)}
						onCancel={() => { setSearchInput(''); setSearch(''); }}
					/>
				</div>

				<div className="overview__content">
					<DataTable
						columns={tableColumns}
						data={submittals}
						getRowKey={(row) => row.submittal_id}
						onRowClick={(row) => goTo(`/submittals/${row.submittal_id}`)}
						rowActions={getMenuItems}
						getRowProps={(row) => ({ id: 'submittals-row-id', 'data-status': row.status })}
						sortKey={sortConfig.key}
						sortDirection={sortConfig.direction ? 'asc' : 'desc'}
						onSort={handleSort}
						loading={loading}
						error={error}
						emptyMessage="No new submission created yet."
						emptyIcon={<IconInfoEmpty width={32} height={32} />}
						pagination={showPagination ? {
							currentPage,
							totalPages,
							totalEntries,
							pageLimit: 10,
							onPageChange: setCurrentPage,
						} : undefined}
					/>
				</div>
			</div>
			<Modal visible={isDeleteModalVisible} variant="medium" close={hideDeleteModal}>
				<CardConfigure>
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__left">
										<div className="card-configure__icon card-configure__icon--error submittals-delete-icon">
											<IconInfo />
										</div>
									</div>
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">Delete Draft Submission</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className="submittals-delete-body">
								All progress made will be lost.<br />Proceed anyway?
							</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Left>
							<Button textWarning noborder nobackground nohover text="Cancel" submit={hideDeleteModal} />
						</CardConfigure.Footer.Left>
						<CardConfigure.Footer.Right>
							<Button
								error
								round
								text={deleteModalText}
								submit={handleDeleteConfirm}
								disabled={isDeleting}
							/>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>
		</div>
	);
};

export default SubmittalsPage;
